import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cpFetch, createAuthHeaders } from '@/lib/cpClient';
import { buildEventSourcePath } from '@/lib/eventSourceAuth';
import { useAuthStore } from '@/store/authStore';
import { useSharedServiceHealth } from '@/lib/ServiceHealthContext';
import type {
  KongCatalogPayload,
  McpEndpointRow,
  ObserveStatRow,
  ObserveStreamEvent,
  SloBreachRow,
  UptimePayload,
  IncidentRow,
} from './types';
import { EXPOSE_LIVE_FEED_SSE_PATH, EXPOSE_LIVE_FEED_WAIT_MS } from './exposeLiveFeedHonesty';

function useAuthHeaders() {
  const { token, selectedTenant } = useAuthStore();
  return token ? createAuthHeaders(token, selectedTenant || 'dev') : null;
}

export function useServicesStatus() {
  // Read-only: the chrome provider owns the only /services/status poller.
  const shared = useSharedServiceHealth();
  return {
    ...shared.query,
    data: shared.payload,
  };
}

export function useKongCatalog() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['expose-health', 'apis-catalog'],
    enabled: Boolean(headers),
    retry: 1,
    queryFn: async () => cpFetch('/apis/catalog', { headers: headers! }) as KongCatalogPayload,
  });
}

export function useMcpEndpoints() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['expose-health', 'mcp-endpoints'],
    enabled: Boolean(headers),
    refetchInterval: 30_000,
    retry: 1,
    queryFn: async () => {
      const resp = await cpFetch('/api-expose/mcp/endpoints', { headers: headers! });
      return (resp?.endpoints || []) as McpEndpointRow[];
    },
  });
}

export function useObserveStats24h() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['expose-health', 'observe-stats', '24h'],
    enabled: Boolean(headers),
    refetchInterval: 30_000,
    retry: 1,
    queryFn: async () => {
      const resp = await cpFetch('/services/api-observe/stats?window=24h', { headers: headers! });
      return {
        has_data: Boolean(resp?.has_data),
        stats: (resp?.stats || []) as ObserveStatRow[],
      };
    },
  });
}

export function useMcpAppStats(appId: string | null) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['expose-health', 'mcp-app-stats', appId],
    enabled: Boolean(headers && appId),
    queryFn: async () => cpFetch(`/api-expose/mcp/endpoints/${encodeURIComponent(appId!)}/stats`, { headers: headers! }),
  });
}

export function useUptimeTimeline(connectionId: string | null) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['expose-health', 'uptime', connectionId],
    enabled: Boolean(headers && connectionId),
    queryFn: async () => {
      const resp = await cpFetch(
        `/services/api-observe/uptime/${encodeURIComponent(connectionId!)}?window=7d`,
        { headers: headers! }
      );
      return resp as UptimePayload & { ok?: boolean };
    },
  });
}

export function useOpenIncidents() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['expose-health', 'incidents', 'open'],
    enabled: Boolean(headers),
    refetchInterval: 30_000,
    retry: 1,
    queryFn: async () => {
      const resp = await cpFetch('/incidents?status=open&limit=20', { headers: headers! });
      return (resp?.rows || []) as IncidentRow[];
    },
  });
}

export function useSloBreaches() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['expose-health', 'slo-breaches'],
    enabled: Boolean(headers),
    refetchInterval: 30_000,
    retry: 1,
    queryFn: async () => (await cpFetch('/slo/breaches', { headers: headers! })) as SloBreachRow[],
  });
}

const MAX_SSE_EVENTS = 50;

export function useObserveEventStream(enabled = true) {
  const [events, setEvents] = useState<ObserveStreamEvent[]>([]);
  const [hasSseUrl, setHasSseUrl] = useState(true);
  const [streamOpen, setStreamOpen] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const esRef = useRef<EventSource | null>(null);
  const eventCountRef = useRef(0);

  const append = useCallback((ev: ObserveStreamEvent) => {
    eventCountRef.current += 1;
    setEvents((prev) => {
      const next = [...prev, ev];
      return next.length > MAX_SSE_EVENTS ? next.slice(next.length - MAX_SSE_EVENTS) : next;
    });
  }, []);

  const retry = useCallback(() => {
    setEvents([]);
    eventCountRef.current = 0;
    setRetryNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStreamOpen(false);
      setStreamError(false);
      setTimedOut(false);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    eventCountRef.current = 0;
    setStreamOpen(false);
    setStreamError(false);
    setTimedOut(false);

    const url = buildEventSourcePath(EXPOSE_LIVE_FEED_SSE_PATH, {
      since: new Date(Date.now() - 60_000).toISOString(),
    });
    if (!url) {
      setHasSseUrl(false);
      return undefined;
    }
    setHasSseUrl(true);

    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    const es = new EventSource(url);
    esRef.current = es;

    const markOpen = () => {
      if (!cancelled) setStreamOpen(true);
    };
    es.onopen = markOpen;

    es.onmessage = (msg) => {
      try {
        append(JSON.parse(msg.data) as ObserveStreamEvent);
      } catch {
        // ignore malformed payloads — comments/heartbeats never reach here
      }
    };

    es.onerror = () => {
      if (cancelled) return;
      setStreamError(true);
      es.close();
      esRef.current = null;
    };

    const waitTimer = setTimeout(() => {
      if (cancelled) return;
      setTimedOut(true);
      if (eventCountRef.current === 0) {
        cancelled = true;
        es.close();
        esRef.current = null;
      }
    }, EXPOSE_LIVE_FEED_WAIT_MS);

    return () => {
      cancelled = true;
      clearTimeout(waitTimer);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [enabled, retryNonce, append]);

  return { events, hasSseUrl, streamOpen, streamError, timedOut, retry };
}
